"use client";

import * as React from "react";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { Dialog as DialogRoot } from "@repo/ui/components/ui/dialog";
import { cn } from "@repo/ui/lib/utils";

export type CommonDialogSize = "sm" | "md" | "lg" | "xl" | "full";

export interface CommonDialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;

  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;

  footer?: React.ReactNode;
  primaryButton?: React.ReactNode;
  secondaryButton?: React.ReactNode;

  size?: CommonDialogSize;
  fullScreen?: boolean;

  showCloseButton?: boolean;
  closeButton?: React.ReactNode;
  closeButtonClassName?: string;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;

  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  overlayClassName?: string;

  titleClassName?: string;
  descriptionClassName?: string;
  closeIconClassName?: string;
  testId?: string;
}

function getSizeClass(size?: CommonDialogSize, fullScreen?: boolean): string {
  if (fullScreen || size === "full") {
    return "sm:max-w-[95vw] md:max-w-[90vw] lg:max-w-[85vw] xl:max-w-[1280px] h-[95vh]";
  }
  switch (size) {
    case "sm":
      return "sm:max-w-sm";
    case "md":
      return "sm:max-w-md";
    case "lg":
      return "sm:max-w-lg";
    case "xl":
      return "sm:max-w-2xl";
    default:
      return "sm:max-w-lg";
  }
}

export function CommonDialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  primaryButton,
  secondaryButton,
  size = "lg",
  fullScreen,
  showCloseButton = true,
  closeButton,
  closeButtonClassName = "bg-brand-blue",
  closeOnOverlayClick = true,
  closeOnEsc = true,
  contentClassName,
  headerClassName,
  footerClassName,
  overlayClassName,
  titleClassName,
  descriptionClassName,
  closeIconClassName,
  testId = "common-dialog",
}: Readonly<CommonDialogProps>) {
  const contentSizeClass = getSizeClass(size, fullScreen);

  // Controlled vs uncontrolled Radix Dialog
  const rootProps =
    typeof open === "boolean" ? { open, onOpenChange } : { defaultOpen, onOpenChange };

  return (
    <DialogRoot {...rootProps}>
      {trigger ? <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger> : null}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn("fixed inset-0 z-50 bg-black/50", overlayClassName)}
        />
        <DialogPrimitive.Content
          className={cn(
            "bg-background fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg",
            contentSizeClass,
            contentClassName,
          )}
          onEscapeKeyDown={(event) => {
            if (!closeOnEsc) event.preventDefault();
          }}
          onPointerDownOutside={(event) => {
            if (!closeOnOverlayClick) event.preventDefault();
          }}
          data-testid={`${testId}-modal`}
        >
          {/* Ensure Radix accessibility requirement: always provide a Dialog Title */}
          {!title && !description ? (
            <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>
          ) : null}

          {(title || description) && (
            <div className={cn("flex flex-col gap-2 text-center sm:text-left", headerClassName)}>
              {title ? (
                <DialogPrimitive.Title
                  className={cn("text-lg leading-none font-semibold", titleClassName)}
                  data-testid={`${testId}-title-lbl`}
                >
                  {title}
                </DialogPrimitive.Title>
              ) : null}
              {description ? (
                <DialogPrimitive.Description
                  className={cn("text-muted-foreground text-sm", descriptionClassName)}
                  data-testid={`${testId}-description-lbl`}
                >
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
          )}

          {children}

          {(footer || primaryButton || secondaryButton) && (
            <div
              className={cn(
                "mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
                footerClassName,
              )}
              data-testid={`${testId}-footer-section`}
            >
              {footer ?? (
                <>
                  {secondaryButton}
                  {primaryButton}
                </>
              )}
            </div>
          )}

          {showCloseButton && (
            <DialogPrimitive.Close asChild>
              {closeButton ?? (
                <button
                  className={cn(
                    "absolute top-4 right-4 cursor-pointer rounded-full p-1 text-white",
                    closeButtonClassName,
                  )}
                  data-testid={`${testId}-close-btn`}
                >
                  <X className={cn("h-4 w-4", closeIconClassName)} />
                  <span className="sr-only">Close</span>
                </button>
              )}
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogRoot>
  );
}

export const CommonDialogTrigger = DialogPrimitive.Trigger;

export default CommonDialog;
